import React, { useEffect, useMemo, useState } from "react";
import {
  FiArrowRight,
  FiClock,
  FiFeather,
  FiPackage,
  FiShield,
  FiTruck,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import PageMeta from "./PageMeta";
import ProductCard from "./ProductCard";
import "../css/main.scss";

const getMonthEnd = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0);
};

const useCountdown = () => {
  const deadline = useMemo(getMonthEnd, []);
  const [remaining, setRemaining] = useState(deadline.getTime() - Date.now());

  useEffect(() => {
    const timer = window.setInterval(
      () => setRemaining(Math.max(0, deadline.getTime() - Date.now())),
      1000
    );
    return () => window.clearInterval(timer);
  }, [deadline]);

  const totalSeconds = Math.floor(remaining / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
};

const serviceCards = [
  {
    title: "Monthly Offer",
    copy: "Pre-order our widest producer selection at friendly group-buy prices.",
    to: "/shop?collection=monthly",
    image: "/main/monthly-offer.png",
    tone: "yellow",
  },
  {
    title: "The Shop",
    copy: "Cheese, cold cuts and pantry essentials ready for next-day delivery.",
    to: "/shop",
    image: "/main/shop-category.png",
    tone: "teal",
  },
  {
    title: "Seasonal Boxes",
    copy: "A thoughtful rotation of fruit, pairings and table-ready discoveries.",
    to: "/shop?category=Fruit%20%26%20Veg",
    image: "/main/seasonal-box.png",
    tone: "coral",
  },
];

const values = [
  {
    icon: FiShield,
    title: "Premium",
    copy: "Small producers, traditional methods and ingredients selected for flavour.",
  },
  {
    icon: FiClock,
    title: "Fresh",
    copy: "Short supply chains and chilled handling keep every delivery in condition.",
  },
  {
    icon: FiPackage,
    title: "Fairly priced",
    copy: "Direct sourcing means more of your spend goes into the food itself.",
  },
  {
    icon: FiFeather,
    title: "Curious",
    copy: "A changing edit for daily cooking, quiet suppers and generous gatherings.",
  },
];

const testimonials = [
  {
    quote:
      "The kind of delivery that turns an ordinary Friday into a proper cheese night. Beautifully packed and genuinely fresh.",
    name: "Maya L.",
  },
  {
    quote:
      "I came for the Comté and now the cultured butter, crackers and orchard drinks are permanent fixtures in our kitchen.",
    name: "Daniel K.",
  },
  {
    quote:
      "Excellent curation without the intimidating language. The pairing notes make choosing something new very easy.",
    name: "Joanne W.",
  },
];

function Main() {
  const countdown = useCountdown();
  const featured = productData.filter((product) => product.bestseller).slice(0, 4);

  return (
    <main className="home-page">
      <PageMeta />
      <section className="home-hero" style={{ backgroundImage: "url(/main/hero-generated.png)" }}>
        <div className="home-hero__overlay" />
        <div className="container home-hero__content">
          <p className="eyebrow eyebrow--light">From the maker to your table</p>
          <h1>
            Gourmet food,
            <br />friendly price.
          </h1>
          <p className="home-hero__lead">
            Artisan cheese and pantry favourites, selected with care and
            delivered chilled across Hong Kong.
          </p>
          <div className="home-hero__actions">
            <Link className="button button--primary" to="/shop">
              Shop the collection <FiArrowRight />
            </Link>
            <Link className="text-link text-link--light" to="/about">
              Meet our producers
            </Link>
          </div>
        </div>
        <a className="hero-scroll" href="#monthly" aria-label="Scroll to monthly offer">
          <span />
          Discover
        </a>
      </section>

      <section className="monthly-banner" id="monthly">
        <div className="container monthly-banner__inner">
          <div>
            <p className="eyebrow">Group order · This month</p>
            <h2>Our monthly offer closes in</h2>
          </div>
          <div className="countdown" aria-label="Offer countdown">
            {Object.entries(countdown).map(([label, value]) => (
              <div key={label}>
                <strong>{String(value).padStart(2, "0")}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>
          <Link className="button button--dark" to="/shop?collection=monthly">
            Order now <FiArrowRight />
          </Link>
        </div>
      </section>

      <section className="service-section section-space">
        <div className="container service-grid">
          {serviceCards.map((card) => (
            <Link
              key={card.title}
              to={card.to}
              className={`service-card service-card--${card.tone}`}
            >
              <div className="service-card__image">
                <img src={card.image} alt="" loading="lazy" />
              </div>
              <div className="service-card__body">
                <h2>{card.title}</h2>
                <p>{card.copy}</p>
                <span>Shop now <FiArrowRight /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="featured-section section-space">
        <div className="container">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Chosen by the club</p>
              <h2>Most-loved this week</h2>
            </div>
            <Link className="text-link" to="/shop?collection=bestsellers">
              View all <FiArrowRight />
            </Link>
          </div>
          <div className="product-grid product-grid--four">
            {featured.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      <section className="values-section section-space">
        <div className="container">
          <div className="section-heading section-heading--center">
            <p className="eyebrow">Why {siteConfig.brandName}?</p>
            <h2>Better food, a shorter journey</h2>
          </div>
          <div className="values-grid">
            {values.map(({ icon: Icon, title, copy }) => (
              <article key={title} className="value-card">
                <Icon />
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="conscious-banner">
        <div className="container conscious-banner__inner">
          <div className="conscious-banner__icon"><FiTruck /></div>
          <div>
            <p className="eyebrow">Consciously delivered</p>
            <h2>Cold when it should be. Light where it can be.</h2>
            <p>
              Insulated reusable liners, right-sized boxes and consolidated routes
              help us protect both delicate food and the journey it takes.
            </p>
          </div>
          <Link className="text-link" to="/shipping">How delivery works <FiArrowRight /></Link>
        </div>
      </section>

      <section className="testimonials-section section-space">
        <div className="container">
          <div className="section-heading section-heading--center">
            <p className="eyebrow">Tried by the club</p>
            <h2>Notes from happy tables</h2>
          </div>
          <div className="testimonial-grid">
            {testimonials.map((testimonial) => (
              <blockquote key={testimonial.name}>
                <div aria-label="5 stars">★★★★★</div>
                <p>“{testimonial.quote}”</p>
                <cite>{testimonial.name}</cite>
              </blockquote>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default Main;
